<script setup lang="ts">
import type { MediaSummary } from '../../../shared/contracts'
import { Bookmark, Clock3 } from '@lucide/vue'
import { computed } from 'vue'
import { rememberMediaSummary } from '@/services/mediaCache'
import { useLibraryStore } from '@/stores/library'
import { detailLocation } from '@/utils/navigation'
import PosterArt from '../media/PosterArt.vue'

const props = defineProps<{ media: MediaSummary, from?: string }>()
const library = useLibraryStore()
const favorite = computed(() => library.isFavorite(props.media))
const location = computed(() => detailLocation(props.media, props.from || '/'))
</script>

<template>
    <section class="border-b border-[var(--c-border)] bg-[var(--c-canvas)]">
        <div class="grid grid-cols-[minmax(0,1fr)_minmax(116px,38vw)] mx-auto max-w-[1480px] min-h-[260px] items-center gap-5 px-5 py-10 lg:grid-cols-[minmax(0,1fr)_340px] sm:grid-cols-[minmax(0,1fr)_minmax(190px,32vw)] lg:min-h-[620px] sm:min-h-[520px] lg:gap-20 sm:gap-10 lg:px-12 lg:py-16 sm:px-8 sm:py-14">
            <div class="reveal-up max-w-3xl min-w-0">
                <p class="mb-4 text-xs text-[var(--accent)] font-700 sm:mb-5 sm:text-sm">
                    {{ media.sourceName }}
                </p>
                <RouterLink :to="location" class="inline-block focus-ring" @click="rememberMediaSummary(media)">
                    <h1 class="max-w-[14ch] text-balance text-[clamp(2rem,6vw,4.75rem)] text-[var(--c-text)] font-400 leading-[1.02] font-editorial transition hover:text-[var(--accent)]">
                        {{ media.title }}
                    </h1>
                </RouterLink>
                <div class="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-[var(--c-text-muted)] sm:mt-6 sm:gap-x-4 sm:text-base">
                    <span v-if="media.score" class="text-[var(--accent)] font-700">★ {{ media.score }}</span>
                    <span>{{ media.year || '年份未知' }}</span>
                    <span v-if="media.runtime" class="hidden items-center gap-1.5 sm:flex"><Clock3 class="size-3.5" />{{ media.runtime }}</span>
                    <span v-if="media.category" class="hidden sm:inline">{{ media.category }}</span>
                </div>
                <RouterLink v-if="media.description" :to="location" class="line-clamp-3 mt-6 hidden max-w-2xl text-[var(--c-text-soft)] leading-8 transition sm:block sm:text-base hover:text-[var(--c-text)] focus-ring" @click="rememberMediaSummary(media)">
                    {{ media.description }}
                </RouterLink>
            </div>

            <div class="reveal-up relative max-w-[340px] w-full justify-self-end" style="animation-delay: 80ms">
                <RouterLink :to="location" class="group block aspect-[2/3] w-full overflow-hidden border border-[var(--c-border)] rounded-[1.375rem] bg-[var(--c-poster)] shadow-[0_18px_54px_var(--c-shadow)] transition-transform duration-300 sm:rounded-[1.75rem] focus-ring hover:-translate-y-1" @click="rememberMediaSummary(media)">
                    <PosterArt :src="media.poster || media.backdrop" :alt="media.title" eager :shade="false" />
                </RouterLink>
                <button class="absolute right-3 top-3 z-2 grid size-11 place-items-center border border-[var(--c-border)] rounded-full bg-[var(--c-surface-raised)]/88 text-[var(--c-text)] shadow-md transition sm:right-4 sm:top-4 sm:size-12 hover:bg-[var(--c-surface-raised)] focus-ring" :aria-label="favorite ? '取消收藏' : '收藏'" :aria-pressed="favorite" @click="library.toggleFavorite(media)">
                    <Bookmark class="size-5" :class="favorite && 'fill-current text-[var(--accent)]'" />
                </button>
            </div>
        </div>
    </section>
</template>
